import { useState, useEffect } from "react"
import Button from "aldoo-ra/Button"
import Image from "aldoo-ra/Image"
import MenuGroup from "aldoo-ra/MenuGroup"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import PubSub from "pubsub-js"
import Value from "aldoo-ra/Value"
import SiteLanguageSelector from "../SiteLanguageSelector"
import ThemeSwitcher from "aldoo-ra/ThemeSwitcher"
import { navigate } from "vike/client/router"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import { Bars3Icon, XMarkIcon, UserIcon } from "@heroicons/react/24/outline"
import { usePageContext } from "vike-react/usePageContext"
import Localization from "aldoo-ra/Localization"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import AccountButton from "./AccountButton"

function scrollToId(id) {
  const el = document.getElementById(id)
  if (el) { el.scrollIntoView({ behavior: "instant" }); return }
  let attempts = 0
  const poll = () => {
    const el = document.getElementById(id)
    if (el) { el.scrollIntoView({ behavior: "instant" }); return }
    if (++attempts < 120) requestAnimationFrame(poll)
  }
  requestAnimationFrame(poll)
}

export default function NavbarMobile({ onContactClick }) {
  const pageContext = usePageContext()
  const currentPath = pageContext?.urlPathname || (typeof window !== "undefined" ? window.location.pathname : "/")
  const { currentLanguage } = Localization()
  const localeRoot = currentLanguage && currentLanguage !== "en" ? `/${currentLanguage}` : "/"
  const texts = TextBundle({ id: "signin" })
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [auth] = PlatformAuth.useAuth()
  const [profile] = Value("user-profile")

  const closeMenu = () => setIsMenuOpen(false)
  const isOnHome = currentPath === localeRoot || currentPath === localeRoot + "/"

  useEffect(() => {
    setMounted(true)
    const handleScroll = () => {
      if (document.body.style.position === "fixed") return
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Disable body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = originalStyle
      }
    }
  }, [isMenuOpen])

  const initial = (profile?.name || auth?.email || "?")[0].toUpperCase()

  return (
    <>
      {/* Mobile menu overlay */}
      {isMenuOpen && (
        <div className="fixed top-16 inset-x-0 bottom-0 z-40 bg-paper backdrop-blur-xl px-4 pt-6 flex flex-col gap-4 overflow-y-auto">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault()
              closeMenu()
              if (isOnHome) {
                PubSub.publish("file-share-reset")
              } else {
                navigate("/")
              }
            }}
            className="flex items-center gap-1.5 shrink-0 pl-4"
          >
            <Image
              id="aldoo_transfer_logo_icon"
              className="h-9 w-auto object-contain"
            />
            <span className="text-text font-bold text-xl tracking-tight">
              Aldoo <span className="text-primary">Transfer</span>
            </span>
          </a>

          <MenuGroup
            name={auth ? "HeaderMenuAuth" : "HeaderMenuPublic"}
            renderAs="vlist"
            showMenuMode="click"
            containMenu
            skipRootItem
            itemsAsLinks={false}
            styleOverrides={{
              menu: "space-y-2",
              menu_column: "",
              menu_item: "list-none flex items-center px-2 py-1 rounded-md cursor-pointer",
            }}
            menuItemRender={(item) => {
              const url = item.link?.[0]?.url
              const isContacts = item.name === "NavContacts"
              const isCreate = item.name === "NavCreate"
              const isHashLink = url?.startsWith("#") || url?.startsWith("/#")
              const hashId = url?.startsWith("/#") ? url.slice(2) : url?.startsWith("#") ? url.slice(1) : null

              const handleClick = () => {
                if (isContacts) {
                  onContactClick()
                } else if (isHashLink && hashId) {
                  closeMenu()
                  const isOnHome = currentPath === localeRoot || currentPath === localeRoot + "/"
                  if (isOnHome) {
                    scrollToId(hashId)
                  } else {
                    navigate(localeRoot)
                    scrollToId(hashId)
                  }
                } else if (url) {
                  navigate(url)
                }
                closeMenu()
              }

              return (
                <Button
                  variant={isCreate ? "primary" : "nav_item"}
                  onClick={handleClick}
                >
                  {item?.label}
                </Button>
              )
            }}
          />
        </div>
      )}

      {/* Top bar */}
      <header
        className={`fixed top-0 left-0 right-0 w-full z-50 h-16 flex items-center justify-between px-4 transition-shadow duration-300 ${
          isMenuOpen ? "bg-paper" : isScrolled ? "shadow-md bg-paper/40 backdrop-blur-lg" : ""
        }`}
      >
        <div className="flex gap-2">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault()
              closeMenu()
              navigate("/")
            }}
            className="flex items-center gap-1.5 shrink-0"
          >
            <Image
              id="aldoo_transfer_logo_icon"
              className="h-[30px] w-auto object-contain"
            />
          </a>
          
          {/* Hamburger */}
          <button
            className="p-2 rounded-lg text-text hover:text-link transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen
              ? <XMarkIcon className="w-7 h-7" />
              : <Bars3Icon className="w-7 h-7" />
            }
          </button>
        </div>

        

        {/* Right side */}
        <div className="flex items-center gap-2">
          <SiteLanguageSelector />
          <AccountButton mounted={mounted} auth={auth} initial={initial} profile={profile} texts={texts} />
        </div>
      </header>
    </>
  )
}